/**
 * formulaEval — client-side evaluation of FORMULA nodes so the card updates the
 * instant you change the operator or reorder inputs (no Save/recompute needed).
 *
 * Leaf metric nodes keep their server-computed value/series; formula nodes are
 * combined from their inputs here using the SAME logic as the Event service
 * (Event/controllers/v2/metricTree.controller.js) so the two never disagree.
 */

export function combineValues(op, vals) {
  const nums = (vals || []).map(Number).filter(Number.isFinite);
  if (!nums.length) return null;
  if (op === '+') return nums.reduce((a, b) => a + b, 0);
  if (op === '*') return nums.reduce((a, b) => a * b, 1);
  if (op === '-') return nums.slice(1).reduce((a, b) => a - b, nums[0]);
  if (op === '/') return nums.slice(1).reduce((a, b) => (b === 0 ? a : a / b), nums[0]);
  return null;
}

export function combineSeries(op, list) {
  const fill = op === '*' || op === '/' ? 1 : 0;
  const keys = new Set();
  list.forEach((s) => (s || []).forEach((p) => keys.add(p.t)));
  // `t` is whatever the backend uses for the series' time axis — a numeric epoch
  // in practice, but a bare `.sort()` compares by coercing to strings regardless,
  // which corrupts numeric ordering (e.g. 9000 sorting before 80). `<`/`>` compare
  // numbers numerically and fall back to the previous lexicographic behavior for
  // any non-numeric `t`, so this is correct either way — unlike the bare sort.
  const sorted = [...keys].sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
  const maps = list.map((s) => {
    const m = new Map();
    (s || []).forEach((p) => m.set(p.t, Number(p.v)));
    return m;
  });
  const out = [];
  for (const t of sorted) {
    const vals = maps.map((m) => (m.has(t) ? m.get(t) : fill));
    const v = combineValues(op, vals);
    if (Number.isFinite(v)) out.push({ t, v });
  }
  return out;
}

export function deltaFromSeries(series) {
  const vals = (series || []).map((p) => Number(p.v)).filter(Number.isFinite);
  if (vals.length < 4) return null;
  const h = Math.floor(vals.length / 2);
  const mean = (a) => a.reduce((x, y) => x + y, 0) / a.length;
  const earlier = mean(vals.slice(0, h));
  const recent = mean(vals.slice(vals.length - h));
  if (!earlier) return null;
  return Math.round(((recent - earlier) / earlier) * 1000) / 10;
}

const perWeekFor = (tf) => (tf === 'last_24_hours' ? 24
  : (tf === 'all_time' || tf === 'last_year' || tf === 'custom_range') ? 1 : 7);

export function periodDeltas(series, tf) {
  let vals = (series || []).map((p) => Number(p.v)).filter(Number.isFinite);
  const sorted = [...vals].sort((a, b) => a - b);
  const med = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;
  if (vals.length >= 3 && med && vals[vals.length - 1] < 0.4 * med) vals = vals.slice(0, -1);
  if (vals.length >= 3 && med && vals[0] < 0.4 * med) vals = vals.slice(1);
  const n = vals.length;
  if (n < 2) return { wow_pct: null, qoq_pct: null };
  const perWeek = perWeekFor(tf);
  const perQuarter = perWeek * 13;
  const clamp = (x) => (x == null ? null : Math.max(-999, Math.min(999, x)));
  const pct = (cur, prev) => (prev > 0 ? clamp(Math.round(((cur - prev) / prev) * 1000) / 10) : null);
  const wow = pct(vals[n - 1], vals[n - 1 - perWeek] != null ? vals[n - 1 - perWeek] : vals[n - 2]);
  const qoq = n - 1 - perQuarter >= 0 ? pct(vals[n - 1], vals[n - 1 - perQuarter]) : pct(vals[n - 1], vals[0]);
  return { wow_pct: wow, qoq_pct: qoq };
}

/**
 * dependsOn — does formula node `startId` (transitively) depend on `targetId`
 * through its formula inputs? Walks the input graph with a visiting guard.
 * `inputsById` maps nodeId → array of input node ids.
 */
function dependsOn(startId, targetId, inputsById, seen = new Set()) {
  const inputs = inputsById.get(startId) || [];
  for (const inId of inputs) {
    if (inId === targetId) return true;
    if (seen.has(inId)) continue;
    seen.add(inId);
    if (dependsOn(inId, targetId, inputsById, seen)) return true;
  }
  return false;
}

/**
 * computeBlockedInputIds — for a formula node `nodeId`, the set of node ids that
 * MUST NOT be added as inputs because doing so would create a circular reference
 * (BUG-MT-05). That is: the node itself, plus every node that already (transitively)
 * depends on `nodeId`. Adding any of those as an input would close a cycle.
 *
 * `nodes` are React Flow nodes ({ id, data:{ kind, formula:{ inputs } } }).
 * Returns a Set<string>. Pure — safe to unit-test and to call from the picker.
 */
export function computeBlockedInputIds(nodes, nodeId) {
  const inputsById = new Map();
  nodes.forEach((n) => {
    inputsById.set(n.id, (n.data?.kind === 'formula' && Array.isArray(n.data?.formula?.inputs))
      ? n.data.formula.inputs : []);
  });
  const blocked = new Set([nodeId]);
  nodes.forEach((n) => {
    if (n.id === nodeId) return;
    // If n already depends on nodeId, then nodeId -> n would be a back-edge (cycle).
    if (dependsOn(n.id, nodeId, inputsById)) blocked.add(n.id);
  });
  return blocked;
}

/**
 * findFormulaCycle — returns the id/label of a formula node that participates in a
 * circular reference, or null if the formula graph is acyclic. Used to block Save
 * with a clear error instead of letting a cycle silently evaluate to 0 (BUG-MT-05).
 */
export function findFormulaCycle(nodes) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const inputsById = new Map();
  nodes.forEach((n) => {
    inputsById.set(n.id, (n.data?.kind === 'formula' && Array.isArray(n.data?.formula?.inputs))
      ? n.data.formula.inputs : []);
  });
  const state = new Map(); // undefined | 'visiting' | 'done'
  let culprit = null;
  const dfs = (id) => {
    if (state.get(id) === 'done') return false;
    if (state.get(id) === 'visiting') { culprit = id; return true; }
    state.set(id, 'visiting');
    for (const inId of (inputsById.get(id) || [])) {
      if (byId.has(inId) && dfs(inId)) return true;
    }
    state.set(id, 'done');
    return false;
  };
  for (const n of nodes) {
    if (dfs(n.id)) {
      const node = byId.get(culprit);
      return { id: culprit, label: node?.data?.label || culprit };
    }
  }
  return null;
}

/**
 * Evaluate every formula node from its inputs (React Flow nodes with .data).
 * Returns { [nodeId]: { value, series, delta_pct, wow_pct, qoq_pct } } for formula
 * nodes only. Memoised + cycle-guarded.
 */
export function evalFormulaNodes(nodes, tf) {
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n; });
  const memo = {};
  const visiting = new Set();

  const ev = (id) => {
    if (memo[id]) return memo[id];
    const n = byId[id];
    if (!n) return { value: null, series: [] };
    const d = n.data || {};
    if (d.kind !== 'formula') {
      return (memo[id] = {
        value: d.value == null ? null : Number(d.value),
        series: Array.isArray(d.series) ? d.series : [],
      });
    }
    if (visiting.has(id)) return (memo[id] = { value: null, series: [] });
    visiting.add(id);
    const inputs = (d.formula && d.formula.inputs) || [];
    const parts = inputs.map(ev);
    visiting.delete(id);
    const op = (d.formula && d.formula.operator) || '+';
    const value = combineValues(op, parts.map((p) => p.value));
    const series = combineSeries(op, parts.map((p) => p.series));
    return (memo[id] = { value, series });
  };

  const out = {};
  nodes.forEach((n) => {
    if (n.data && n.data.kind === 'formula') {
      const { value, series } = ev(n.id);
      out[n.id] = { value, series, delta_pct: deltaFromSeries(series), ...periodDeltas(series, tf) };
    }
  });
  return out;
}
